'use client'

import { RefObject } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { EASES, DURATIONS } from './eases'
import { useReducedMotion } from './useReducedMotion'

export function useHeroEntry(ref: RefObject<HTMLElement | null>) {
  const reducedMotion = useReducedMotion()

  useGSAP(
    () => {
      const el = ref.current
      if (!el) return

      const ALL = [
        '#hero-wisps', '#hero-swarm',
        '#hero-eyebrow', '#hero-subhead', '#hero-ctas > *',
        '#hero-chrome-tl', '#hero-chrome-tr', '#hero-chrome-bl', '#hero-chrome-br',
        '#hero-scroll-cue',
      ]

      if (reducedMotion) {
        const h1 = el.querySelector('#hero-headline') as HTMLElement | null
        const lines = h1 ? Array.from(h1.children) : []
        gsap.set([...ALL, ...lines], { autoAlpha: 1, clearProps: 'transform' })
        return
      }

      // ── Initial hidden states ──────────────────────────────────────────────
      gsap.set(['#hero-wisps', '#hero-swarm'], { autoAlpha: 0 })
      gsap.set(['#hero-chrome-tl', '#hero-chrome-tr'], { autoAlpha: 0, y: -10 })
      gsap.set(['#hero-chrome-bl', '#hero-chrome-br'], { autoAlpha: 0, y: 8 })
      gsap.set(['#hero-eyebrow', '#hero-subhead', '#hero-scroll-cue'], { autoAlpha: 0, y: 14 })
      gsap.set('#hero-ctas > *', { autoAlpha: 0, y: 10 })

      // Headline line spans
      const h1 = el.querySelector('#hero-headline') as HTMLElement | null
      const lines = h1 ? (Array.from(h1.children) as HTMLElement[]) : []
      gsap.set(lines, { autoAlpha: 0, y: 32 })

      // ── Timeline ──────────────────────────────────────────────────────────
      const tl = gsap.timeline({
        defaults: { ease: EASES.CINEMATIC },
      })

      // t=0.00 — Top chrome drops in
      tl.to('#hero-chrome-tl', { autoAlpha: 1, y: 0, duration: DURATIONS.SLOW }, 0)
      tl.to('#hero-chrome-tr', { autoAlpha: 1, y: 0, duration: DURATIONS.SLOW }, 0.08)

      // t=0.20 — Wisps bloom
      tl.to('#hero-wisps', { autoAlpha: 1, duration: DURATIONS.CINEMATIC, ease: 'power1.inOut' }, 0.20)

      // t=0.40 — Eyebrow rises
      tl.to('#hero-eyebrow', { autoAlpha: 1, y: 0, duration: DURATIONS.BASE }, 0.40)

      // t=0.62 → 0.98 — Headline lines stagger (biosoil ease)
      lines.forEach((line, i) => {
        tl.to(line, {
          autoAlpha: 1, y: 0,
          duration: DURATIONS.SLOW, ease: EASES.BIOSOIL,
        }, 0.62 + i * 0.18)
      })

      // t=0.00 — Swarm container fades in early so the scatter→gather
      // materialisation is visible from the start of the page load.
      tl.fromTo('#hero-swarm',
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: DURATIONS.CINEMATIC, ease: 'power1.out' },
        0,
      )

      // t=1.32 — Subhead
      tl.to('#hero-subhead', { autoAlpha: 1, y: 0, duration: DURATIONS.SLOW }, 1.32)

      // t=1.52 — CTAs stagger
      tl.to('#hero-ctas > *', {
        autoAlpha: 1, y: 0,
        duration: DURATIONS.BASE, stagger: 0.10,
      }, 1.52)

      // t=1.64 — Bottom chrome rises
      tl.to('#hero-chrome-bl', { autoAlpha: 1, y: 0, duration: DURATIONS.BASE }, 1.64)
      tl.to('#hero-chrome-br', { autoAlpha: 1, y: 0, duration: DURATIONS.BASE }, 1.72)

      // t=1.84 — Scroll cue
      tl.to('#hero-scroll-cue', { autoAlpha: 1, y: 0, duration: DURATIONS.BASE }, 1.84)
    },
    { scope: ref, dependencies: [reducedMotion], revertOnUpdate: true },
  )
}
